import _ from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SegmentEvent } from 'services/helpers/segment';
import { useSegment } from '../segment';
import { QueryParamConfig, QueryParamConfigMap, DecodedValueMap } from './types';

export function withDefault<D, DefaultType extends D2, D2 = D>(
  param: QueryParamConfig<D, D2>,
  defaultValue: DefaultType,
  includeNull = true
) {
  const decodeWithDefault = (...args: Parameters<typeof param.decode>) => {
    const raw = param.decode(...args);
    let decodedValue = null;
    if (Array.isArray(raw)) {
      decodedValue = raw.map((element) => {
        if (element) {
          return element;
        }
        return null;
      });
    } else {
      decodedValue = raw;
    }

    if (raw === undefined || (includeNull && raw === null)) {
      return defaultValue;
    }

    return decodedValue;
  };
  return { ...param, decode: decodeWithDefault };
}

export function buildSearchParams<QPCMap extends QueryParamConfigMap>(
  paramConfigMap: QPCMap,
  obj: Partial<DecodedValueMap<QPCMap>>,
  onUnusedKey?: (key: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    const rawValue = paramConfigMap[key];
    if (_.isNil(rawValue)) {
      return acc;
    }
    const encodedValue = paramConfigMap[key].encode(value)?.toString();

    if (_.isNil(encodedValue) || encodedValue === '') {
      if (onUnusedKey) {
        onUnusedKey(key);
      }
      return acc;
    }

    return {
      ...acc,
      [key]: encodedValue,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }, {} as any);
}

export function useCustomSearchParams<QPCMap extends QueryParamConfigMap>(
  paramConfigMap: QPCMap,
  resetPageParamOnUpdate = false,
  navigationOptions: {
    replace?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state?: any;
  } = {
    replace: true,
  }
): {
  searchParams: Partial<DecodedValueMap<QPCMap>>;
  setSearchParams: (obj: Partial<DecodedValueMap<QPCMap>>, keepOtherParams?: boolean) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchAsObject = useMemo(
    () => Object.fromEntries(new URLSearchParams(searchParams)),
    [searchParams]
  );

  const transformedSearch = useMemo(
    () =>
      Object.entries(paramConfigMap).reduce(
        (acc, [key, paramType]) => ({
          ...acc,
          [key]: paramType.decode(acc[key]),
        }),
        searchAsObject
      ) as Partial<DecodedValueMap<QPCMap>>,
    [paramConfigMap, searchAsObject]
  );

  const customSetSearchParams = useCallback(
    (obj: Partial<DecodedValueMap<QPCMap>>, keepOtherParams = true): void => {
      const newSearchParams = buildSearchParams(
        paramConfigMap,
        obj,
        (key) => delete searchAsObject[key]
      );

      if (resetPageParamOnUpdate) {
        newSearchParams.page = 1;
      }
      if (keepOtherParams) {
        setSearchParams(
          {
            ...searchAsObject,
            ...newSearchParams,
          },
          navigationOptions
        );
      } else {
        setSearchParams(newSearchParams, navigationOptions);
      }
    },
    [navigationOptions, paramConfigMap, resetPageParamOnUpdate, searchAsObject, setSearchParams]
  );

  return React.useMemo(
    () => ({
      searchParams: transformedSearch,
      setSearchParams: customSetSearchParams,
    }),
    [customSetSearchParams, transformedSearch]
  );
}

export function useTrackedCustomSearchParams<QPCMap extends QueryParamConfigMap>(
  eventTitle: SegmentEvent,
  paramConfigMap: QPCMap,
  resetPageParamOnUpdate = false,
  navigationOptions: {
    replace?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state?: any;
  } = {
    replace: true,
  }
) {
  const { searchParams, setSearchParams } = useCustomSearchParams(
    paramConfigMap,
    resetPageParamOnUpdate,
    navigationOptions
  );
  const { track } = useSegment();

  const trackFilter = React.useCallback(
    (newValues?: Record<string, unknown>) => {
      const { focusedContainer, ...filterParams } = searchParams;
      const filters = { ...filterParams, ...newValues };
      track(eventTitle, filters, { includeUser: true, includeCompany: true });
    },
    [eventTitle, searchParams, track]
  );

  const decoratedSetSearchParams = React.useCallback(
    (params: Partial<DecodedValueMap<QPCMap>>) => {
      setSearchParams(params);
      trackFilter(params);
    },
    [setSearchParams, trackFilter]
  );

  return { searchParams, setSearchParams: decoratedSetSearchParams };
}
